// Phase 7.3 — Drive quota card for /status (superadmin only).
//
// Shows the OAuth account's Drive storage picture: how much the Slack
// archive occupies (a recursive sum of the archive folder tree), how
// much the WHOLE account uses, and how much is free. The archive is
// typically a tiny sliver of a heavily-used account, so the card shows
// all three numbers rather than implying the archive == account usage.
//
// Self-contained: fetches /admin/drive-quota on mount, gates itself on
// the superadmin role (renders nothing otherwise — matching the
// endpoint's own gating), and degrades to a graceful error row on any
// failure so it never breaks the rest of /status.
//
// Visuals reuse the Phase 7.0 tokens already used by the sibling
// /status cards (glass Card surface, mono labels, semantic color
// tokens) — no new colors.

import { useEffect, useState } from "react";

import { ApiError, getDriveQuota, type DriveQuota } from "../lib/api";
import { useAuth } from "../state/AuthContext";
import { useImpersonation } from "../state/ImpersonationContext";
import { formatBytes, formatRelative } from "../lib/timeFmt";
import { Card } from "./ui/Card";

export type DriveQuotaState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: DriveQuota };

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.responseBody as { detail?: unknown } | undefined)?.detail;
    switch (detail) {
      case "drive_disabled":
        return "Drive integration is disabled.";
      case "archive_root_not_found":
        return "Archive folder not found on Drive.";
      case "drive_unavailable":
        return "Drive is unavailable right now — try refreshing.";
    }
    if (err.status === 0) return "Network error reaching the service.";
  }
  return "Could not load Drive quota.";
}

export function DriveQuotaCard() {
  const { role } = useAuth();
  const { current: impersonationTarget } = useImpersonation();
  // Phase 6.2 fidelity: while impersonating, every request is
  // role-downgraded server-side — /admin/drive-quota would 403 — and
  // "viewing as <user>" should show what THEY see (no admin cards).
  const isSuperadmin = role === "superadmin" && impersonationTarget === null;
  const [state, setState] = useState<DriveQuotaState>({ kind: "loading" });

  useEffect(() => {
    if (!isSuperadmin) return;
    const ctrl = new AbortController();
    setState({ kind: "loading" });
    getDriveQuota(ctrl.signal)
      .then((data) => setState({ kind: "ready", data }))
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setState({ kind: "error", message: errorMessage(err) });
      });
    return () => ctrl.abort();
  }, [isSuperadmin]);

  // Superadmin-only surface — render nothing for everyone else.
  if (!isSuperadmin) return null;

  return <DriveQuotaView state={state} />;
}

// Presentational shell — pure function of state, so it renders the same
// in SSR tests as in the browser. The container above owns the fetch.
export function DriveQuotaView({ state }: { state: DriveQuotaState }) {
  return (
    <Card
      variant="glass"
      className="px-5 py-4"
      data-testid="status-drive-quota-card"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        Drive quota
      </div>
      <div className="mt-3">
        {state.kind === "loading" && (
          <div
            data-testid="status-drive-quota-loading"
            className="font-mono text-xs text-theme-text-tertiary"
          >
            Loading…
          </div>
        )}
        {state.kind === "error" && (
          <div
            data-testid="status-drive-quota-error"
            className="font-mono text-xs text-theme-text-tertiary"
          >
            {state.message}
          </div>
        )}
        {state.kind === "ready" && <QuotaBody data={state.data} />}
      </div>
    </Card>
  );
}

function QuotaBody({ data }: { data: DriveQuota }) {
  const { total_bytes, account_usage_bytes, archive_used_bytes, free_bytes } =
    data;
  const unlimited = total_bytes === null;
  const updated = formatRelative(data.computed_at);

  return (
    <div className="space-y-3">
      {unlimited ? (
        <div
          data-testid="status-drive-quota-unlimited"
          className="font-mono text-xs text-theme-text-secondary"
        >
          Archive {formatBytes(archive_used_bytes)}{" "}
          <span className="text-theme-text-tertiary">/ unlimited</span>
        </div>
      ) : (
        <QuotaBar
          total={total_bytes as number}
          account={account_usage_bytes}
          archive={archive_used_bytes}
        />
      )}

      <dl className="grid grid-cols-3 gap-x-2">
        <Stat
          label="Archive"
          value={formatBytes(archive_used_bytes)}
          testid="status-drive-quota-archive"
          accent="text-theme-success-text"
        />
        <Stat
          label="Account used"
          value={formatBytes(account_usage_bytes)}
          testid="status-drive-quota-account"
        />
        <Stat
          label="Free"
          value={free_bytes === null ? "—" : formatBytes(free_bytes)}
          testid="status-drive-quota-free"
        />
      </dl>

      <div
        data-testid="status-drive-quota-updated"
        className="font-mono text-[10px] text-theme-text-muted"
      >
        Updated {updated}
      </div>
    </div>
  );
}

// Stacked bar: archive slice (green) + other account usage (muted) over
// a track whose remaining width reads as free. Honest by construction —
// the filled portion is the FULL account usage against the total cap.
function QuotaBar({
  total,
  account,
  archive,
}: {
  total: number;
  account: number;
  archive: number;
}) {
  const safeTotal = total > 0 ? total : 1;
  const archiveClamped = Math.min(archive, account);
  const other = Math.max(0, account - archiveClamped);
  const archivePct = Math.min(100, (archiveClamped / safeTotal) * 100);
  const otherPct = Math.min(100 - archivePct, (other / safeTotal) * 100);
  const usedPct = Math.min(100, Math.round((account / safeTotal) * 100));

  return (
    <div>
      <div className="font-mono text-xs text-theme-text-secondary">
        {formatBytes(account)}{" "}
        <span className="text-theme-text-tertiary">
          / {formatBytes(total)} ({usedPct}%)
        </span>
      </div>
      <div
        data-testid="status-drive-quota-bar"
        className="mt-2 flex h-1.5 w-full overflow-hidden bg-theme-bg-chip-subtle border border-theme-border-default"
        role="img"
        aria-label={`${usedPct}% of Drive quota used; archive uses ${formatBytes(
          archive,
        )}`}
      >
        <div
          className="h-full bg-theme-success-text"
          style={{
            width: `${archivePct}%`,
            // Keep a nonzero archive at least a hairline wide so its
            // (usually tiny) footprint is still perceptible.
            minWidth: archiveClamped > 0 ? "2px" : undefined,
          }}
        />
        <div
          className="h-full bg-theme-text-tertiary"
          style={{ width: `${otherPct}%` }}
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  testid,
  accent,
}: {
  label: string;
  value: string;
  testid: string;
  accent?: string;
}) {
  return (
    <div>
      <dt className="font-mono text-[9px] uppercase tracking-widest text-theme-text-tertiary">
        {label}
      </dt>
      <dd
        data-testid={testid}
        className={`mt-0.5 font-mono text-sm ${accent ?? "text-theme-text-secondary"}`}
      >
        {value}
      </dd>
    </div>
  );
}
